import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
  RELOAD_CHECK_INTERVAL_MS,
  VERSION_CHECK_INTERVAL_MS,
  blockAppUpdate,
  hasPendingSaves,
  isAppUpdateBlocked,
  parseVersionFile,
  runningAppVersion,
  shouldReloadNow,
  type ReloadTrigger,
} from "./appUpdate";

const sessionStorageReloadedForKey = "app-update-reloaded-for";

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!response.ok) {
      return null;
    }
    return parseVersionFile(await response.json());
  } catch {
    // offline, or version.json is missing: try again later
    return null;
  }
}

// Reload the app when a new version is deployed. Mounted once, in App.
export function useAppAutoUpdate({ enabled }: { enabled: boolean }) {
  const deployedVersion = useRef<string | null>(null);
  const lastInteraction = useRef<number>(0);
  const location = useLocation();

  const tryReload = useRef((trigger: ReloadTrigger) => {
    const reload = shouldReloadNow({
      runningVersion: runningAppVersion,
      deployedVersion: deployedVersion.current,
      alreadyReloadedForVersion: sessionStorage.getItem(sessionStorageReloadedForKey),
      hasPendingSaves: hasPendingSaves(),
      updateBlocked: isAppUpdateBlocked(),
      trigger,
      idleMs: Date.now() - lastInteraction.current,
      pageHidden: document.hidden,
    });
    if (reload && deployedVersion.current !== null) {
      console.log(`Updating app from ${runningAppVersion} to ${deployedVersion.current}`);
      sessionStorage.setItem(sessionStorageReloadedForKey, deployedVersion.current);
      window.location.reload();
    }
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    Sentry.setTag("app_version", runningAppVersion);
    lastInteraction.current = Date.now();

    const checkVersion = async () => {
      deployedVersion.current = (await fetchDeployedVersion()) ?? deployedVersion.current;
      tryReload.current("timer");
    };
    const onInteraction = () => {
      lastInteraction.current = Date.now();
    };
    const onVisibilityChange = () => {
      if (document.hidden) {
        tryReload.current("timer");
      } else {
        checkVersion();
      }
    };

    checkVersion();
    const versionInterval = setInterval(checkVersion, VERSION_CHECK_INTERVAL_MS);
    const reloadInterval = setInterval(() => tryReload.current("timer"), RELOAD_CHECK_INTERVAL_MS);
    const interactionEvents = ["pointerdown", "keydown", "touchstart", "scroll"] as const;
    interactionEvents.forEach((e) =>
      window.addEventListener(e, onInteraction, { passive: true, capture: true }),
    );
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", checkVersion);

    return () => {
      clearInterval(versionInterval);
      clearInterval(reloadInterval);
      interactionEvents.forEach((e) =>
        window.removeEventListener(e, onInteraction, { capture: true }),
      );
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", checkVersion);
    };
  }, [enabled]);

  // a page change is a good moment to reload: nothing is being edited
  useEffect(() => {
    if (enabled) {
      tryReload.current("navigation");
    }
  }, [enabled, location.pathname]);
}

// Prevent the automatic update while `block` is true, when a reload would loose something shown
// to the user that is not stored in the backend.
export function useBlockAppUpdate(block: boolean) {
  useEffect(() => {
    if (block) {
      return blockAppUpdate();
    }
  }, [block]);
}

// A tab opened before a deployment may request a lazy loaded chunk which does not exist anymore.
const sessionStoragePreloadErrorKey = "app-update-preload-error-reload";

export function reloadOnMissingChunk() {
  window.addEventListener("vite:preloadError", (event) => {
    const lastReload = parseInt(sessionStorage.getItem(sessionStoragePreloadErrorKey) ?? "0") || 0;
    // no reload loop if the chunk is really broken
    if (Date.now() - lastReload > 60 * 1000) {
      event.preventDefault();
      sessionStorage.setItem(sessionStoragePreloadErrorKey, `${Date.now()}`);
      window.location.reload();
    }
  });
}
